import React, { useEffect } from 'react';
import {
  Stethoscope, Pill, FlaskConical, Activity, Shield, Syringe,
  FileCheck2, Lock, ListChecks, ClipboardList, FileText, ClipboardCheck,
} from 'lucide-react';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandShortcut,
} from '@/components/ui/command';
import { COCKPIT_NAV_ORDER, LEGACY_TO_SECTION, type CockpitSectionId } from './cockpit-nav';

interface CockpitCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onJump: (section: CockpitSectionId) => void;
  onSign: () => void;
  isSigned: boolean;
}

const ITEMS: Array<{
  id: CockpitSectionId | 'sign';
  label: string;
  shortcut?: string;
  icon: React.ElementType;
  group: 'navigate' | 'action';
}> = [
  { id: 'overview', label: 'Go to Overview', icon: ClipboardList, group: 'navigate' },
  { id: 'rooming', label: 'Open Rooming checklist', icon: ClipboardCheck, group: 'navigate' },
  { id: 'notes', label: 'Open Notes', icon: FileText, group: 'navigate' },
  { id: 'diagnoses', label: 'Add diagnosis', icon: Stethoscope, group: 'navigate' },
  { id: 'rx', label: 'Write prescription', icon: Pill, group: 'navigate' },
  { id: 'orders', label: 'Order labs / imaging', icon: FlaskConical, group: 'navigate' },
  { id: 'vitals', label: 'Capture vitals', icon: Activity, group: 'navigate' },
  { id: 'allergies', label: 'Review allergies', icon: Shield, group: 'navigate' },
  { id: 'medications', label: 'Reconcile medications', icon: Pill, group: 'navigate' },
  { id: 'immunizations', label: 'Record immunization', icon: Syringe, group: 'navigate' },
  { id: 'intake', label: 'Review intake response', icon: ListChecks, group: 'navigate' },
  { id: 'avs', label: 'Open After-Visit Summary', icon: FileCheck2, group: 'navigate' },
  { id: 'sign', label: 'Sign & close visit', icon: Lock, group: 'action' },
];

const CockpitCommandPalette: React.FC<CockpitCommandPaletteProps> = ({
  open, onOpenChange, onJump, onSign, isSigned,
}) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  const visible = ITEMS.filter(i => !(isSigned && i.id === 'sign'));

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder='Type a command or jump to a section…' />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>
        <CommandGroup heading='Navigate'>
          {visible.filter(i => i.group === 'navigate').map(i => {
            const Icon = i.icon;
            // Resolve the (possibly legacy) ID to its current home tab so
            // the keyboard hint matches what actually opens.
            const target = LEGACY_TO_SECTION[i.id as CockpitSectionId] || (i.id as CockpitSectionId);
            const navIdx = COCKPIT_NAV_ORDER.indexOf(target);
            const shortcut = navIdx >= 0 && navIdx < 9 ? String(navIdx + 1) : null;
            return (
              <CommandItem
                key={i.id}
                onSelect={() => {
                  onJump(target);
                  onOpenChange(false);
                }}
              >
                <Icon className='mr-2 h-4 w-4' />
                {i.label}
                {shortcut && <CommandShortcut>{shortcut}</CommandShortcut>}
              </CommandItem>
            );
          })}
        </CommandGroup>
        {!isSigned && (
          <CommandGroup heading='Actions'>
            <CommandItem
              onSelect={() => {
                onSign();
                onOpenChange(false);
              }}
            >
              <Lock className='mr-2 h-4 w-4' />
              Sign &amp; close visit
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default CockpitCommandPalette;
